import React, { useState, ComponentPropsWithoutRef, useEffect, useRef, Ref } from 'react';
import { debounce, isEqual } from 'lodash';
import { EventAutocompleteView, EventResource } from '@mark43/cad-api';
import { useApiClient } from '@m43/core';
import { omitRef } from '../helpers/omit-ref';
import type { OnInputChangeParams } from './types';
import { useFocusManager } from '@react-aria/focus';
import { moveFocusNext } from '../helpers/move-focus';
import { BaseSelect } from './base-select';
import { SelectInstance } from 'react-select';
import { Flex, GroupBase, Icon } from '@cad-arc';

export type EventOption = {
    value: number;
    label: string;
    render: () => React.ReactElement;
    data: EventAutocompleteView;
};

export interface EventAutocompleteSelectProps
    extends Omit<
        ComponentPropsWithoutRef<typeof BaseSelect>,
        'options' | 'isLoading' | 'onChange' | 'value'
    > {
    value?: EventOption | null;
    includeClearedEvents?: boolean;
    /**
     * RND-57782: when true, the BE excludes cleared events older than the
     * department's `cad.cleared_event_search_retention_time` setting from
     * autocomplete results. Defaults to false so older callers keep their
     * existing behavior.
     */
    applyRetention?: boolean;
    initialInputValue?: string;
    defaultValue: null;
    onChange?: OnChangeHandler;
    searchForCallForService?: boolean;
    agencyTypeGlobalAttrIds?: number[];
    selectRef?: Ref<SelectInstance<unknown, false, GroupBase<unknown>> | undefined>;
}

export function mapEventAutocompleteViewToOption(data: EventAutocompleteView): EventOption {
    const { agencyEventId, agencyEventNumber, isActive } = data;
    return {
        value: agencyEventId,
        label: `#${agencyEventNumber}`,
        render: () => {
            return (
                <Flex alignItems="center" justifyContent="space-between" w="full">
                    <div>
                        <span style={{ fontWeight: 'var(--arc-fontWeights-bold)' }}>
                            #{agencyEventNumber}
                        </span>
                        <span
                            style={{
                                marginLeft: 'var(--arc-space-2)',
                                color: 'var(--arc-colors-text-secondary)',
                            }}
                        >
                            {data.cfsText}
                        </span>
                    </div>
                    {isActive === false ? (
                        <Flex title="Cleared event">
                            <Icon icon="Closed" aria-label="Cleared event" color="icon.default" />
                        </Flex>
                    ) : null}
                </Flex>
            );
        },
        data,
    };
}

type EventAutocompleteActions =
    | { type: 'SET_OPTIONS'; options: EventOption[] }
    | { type: 'SET_LOADING' }
    | { type: 'SET_ERROR'; error: string };
type EventAutocompleteSelect =
    | { options: EventOption[]; loading: false; error: undefined }
    | { options: EventOption[]; loading: true; error: undefined }
    | { options: EventOption[]; loading: false; error: string };

type OnChangeHandler = (value: EventOption | null) => void;

export const MINIMUM_SEARCH_STRING_LENGTH = 3;

export function EventAutocomplete(props: EventAutocompleteSelectProps): React.ReactElement {
    const apiClient = useApiClient();
    const [inputHasValue, setInputHasValue] = useState<boolean>(false);
    const { defaultValue, value, emptyState, selectRef, ...rest } = props;
    const hasFetchedInitialValue = useRef(false);

    const [state, dispatch] = React.useReducer(
        (
            state: EventAutocompleteSelect,
            action: EventAutocompleteActions
        ): EventAutocompleteSelect => {
            switch (action.type) {
                case 'SET_OPTIONS':
                    return {
                        loading: false,
                        error: undefined,
                        options: action.options,
                    };
                case 'SET_LOADING':
                    return {
                        loading: true,
                        error: undefined,
                        options: [],
                    };
                case 'SET_ERROR':
                    return {
                        loading: false,
                        error: action.error,
                        options: [],
                    };
                default:
                    return state;
            }
        },
        { options: [], loading: false, error: undefined }
    );

    const { options } = state;
    const { onChange } = props;
    const onChangeMemo = React.useMemo(() => {
        return (
            e: Parameters<
                Exclude<React.ComponentProps<typeof BaseSelect>['onChange'], undefined>
            >[0]
        ) => {
            const selectedOption = options.find((option) => option.value === e.target.value);
            onChange?.(selectedOption ?? defaultValue);
        };
    }, [onChange, options, defaultValue]);

    const fetchOptions = React.useMemo(() => {
        return debounce(async (input: string, selectFirst?: boolean) => {
            if (input.trim().length < MINIMUM_SEARCH_STRING_LENGTH) {
                return;
            }
            try {
                dispatch({ type: 'SET_LOADING' });
                const eventAutocompleteResponse = await apiClient.request<EventResource.GetEventsByAutocomplete>(
                    {
                        method: 'POST',
                        data: {
                            query: input.trim(),
                            searchCallForService: props.searchForCallForService ?? false,
                            includeClearedEvents: props.includeClearedEvents ?? true,
                            applyRetention: props.applyRetention ?? false,
                            agencyTypeGlobalAttrIds: props.agencyTypeGlobalAttrIds ?? [],
                        },
                        url: 'event/autocomplete',
                    }
                );
                if (eventAutocompleteResponse.data) {
                    const eventAutocompleteViews: EventAutocompleteView[] =
                        eventAutocompleteResponse.data.data?.eventAutocompleteViews ?? [];
                    dispatch({
                        type: 'SET_OPTIONS',
                        options: eventAutocompleteViews.map(mapEventAutocompleteViewToOption),
                    });
                    const prefillOption = eventAutocompleteViews?.[0];
                    if (selectFirst && prefillOption) {
                        onChange?.(mapEventAutocompleteViewToOption(prefillOption));
                    }
                } else {
                    dispatch({ type: 'SET_OPTIONS', options: [] });
                }
            } catch (e) {
                dispatch({
                    type: 'SET_ERROR',
                    error: 'Failed to retrieve user options',
                });
            }
        }, 250);
    }, [
        apiClient,
        props.searchForCallForService,
        props.includeClearedEvents,
        props.applyRetention,
        props.agencyTypeGlobalAttrIds,
        onChange,
    ]);

    const onInputChangeMemo = React.useMemo(() => {
        return (e: OnInputChangeParams[0], newValue: OnInputChangeParams[1]) => {
            setInputHasValue(!!newValue);
            void fetchOptions(newValue);
        };
    }, [fetchOptions]);

    useEffect(() => {
        if (props.initialInputValue && !hasFetchedInitialValue.current) {
            hasFetchedInitialValue.current = true;
            void fetchOptions(props.initialInputValue, true);
        }
    }, [fetchOptions, props.initialInputValue]);

    const focusManager = useFocusManager();

    return (
        <BaseSelect
            {...omitRef(rest)}
            filterOption={() => true}
            isLoading={state.loading}
            value={value ?? defaultValue}
            emptyState={inputHasValue ? 'No results found' : emptyState ?? 'Type to search'}
            options={state.options}
            isClearable={!isEqual(value, defaultValue)}
            onInputChange={onInputChangeMemo}
            onChange={(e) => {
                onChangeMemo(e);
                moveFocusNext(focusManager, e.currentTarget.value, false);
            }}
            matchSort={false}
            // @ts-expect-error ref type mismatch but it works
            ref={selectRef}
        />
    );
}
